/**
 * BlueBuck Wishlist & Favorites System
 * Handles localStorage persistence, dynamic badge updating,
 * and global heart toggle buttons across header and product cards.
 */
(function () {
  'use strict';

  const PRIMARY_KEY = 'shopify_liked_products';
  const LEGACY_KEY = 'bluebuck_wishlist';

  // Get current wishlist array from localStorage
  function getWishlist() {
    try {
      const primaryData = localStorage.getItem(PRIMARY_KEY);
      if (primaryData) {
        return JSON.parse(primaryData);
      }
      const legacyData = localStorage.getItem(LEGACY_KEY);
      if (legacyData) {
        const parsed = JSON.parse(legacyData);
        localStorage.setItem(PRIMARY_KEY, JSON.stringify(parsed));
        return parsed;
      }
      return [];
    } catch (e) {
      console.error('Error reading wishlist from localStorage:', e);
      return [];
    }
  }

  // Save wishlist array to localStorage
  function saveWishlist(items) {
    try {
      const jsonStr = JSON.stringify(items);
      localStorage.setItem(PRIMARY_KEY, jsonStr);
      localStorage.setItem(LEGACY_KEY, jsonStr);
    } catch (e) {
      console.error('Error saving wishlist to localStorage:', e);
    }
  }

  // Check if an identifier (handle or ID) is in wishlist
  function isInWishlist(identifier) {
    if (!identifier) return false;
    const wishlist = getWishlist();
    const strId = String(identifier).trim();
    return wishlist.some(item => {
      if (typeof item === 'string') return item === strId;
      if (typeof item === 'object' && item !== null) {
        return String(item.handle) === strId || String(item.id) === strId;
      }
      return false;
    });
  }

  // Toggle item in wishlist
  function toggleWishlist(productData) {
    if (!productData) return { added: false, count: 0 };
    let handle = '';
    let id = '';

    if (typeof productData === 'string') {
      handle = productData.trim();
    } else if (typeof productData === 'object' && productData !== null) {
      handle = productData.handle || productData.id || '';
      id = productData.id || '';
    }

    if (!handle && !id) return { added: false, count: 0 };

    const wishlist = getWishlist();
    const targetKey = handle || id;
    const index = wishlist.findIndex(item => {
      if (typeof item === 'string') return item === targetKey || item === id;
      if (typeof item === 'object' && item !== null) {
        return (handle && item.handle === handle) || (id && item.id === id);
      }
      return false;
    });

    let added = false;
    if (index > -1) {
      wishlist.splice(index, 1);
      added = false;
    } else {
      wishlist.push(targetKey);
      added = true;
    }

    saveWishlist(wishlist);
    syncWishlistUI();

    // Dispatch global event
    window.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        detail: {
          added,
          handle: targetKey,
          count: wishlist.length,
          wishlist
        }
      })
    );

    return { added, count: wishlist.length };
  }

  // Synchronize all UI elements (badges & heart buttons)
  function syncWishlistUI() {
    const wishlist = getWishlist();
    const count = wishlist.length;

    // 1. Update Header & Navigation Badge Counters
    const badgeSelectors = [
      '#wishlist-count',
      '#wishlist-header-count',
      '.wishlist-badge',
      '.wishlist-badge-count',
      '.header-wishlist-badge',
      '.lt-wishlist-count-badge',
      '[data-wishlist-count]'
    ];

    badgeSelectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(badge => {
        badge.textContent = count;
        if (count > 0) {
          badge.style.display = 'inline-flex';
          badge.classList.add('has-items');
        } else {
          badge.style.display = 'none';
          badge.classList.remove('has-items');
        }
      });
    });

    // 2. Update all Heart Toggle Buttons on page
    const btnSelectors = [
      '[data-wishlist-btn]',
      '.wishlist-btn',
      '.bluebuck-trend-wishlist',
      '.bluebuck-wishlist-btn',
      '.bluebuck-bs-wishlist',
      '.rp-wishlist-btn',
      '.bb-item-wish-btn',
      '.product-card__wishlist',
      '.i3d-wishlist-btn',
      '.lt-card-wishlist-btn'
    ];

    document.querySelectorAll(btnSelectors.join(',')).forEach(btn => {
      const handle =
        btn.getAttribute('data-product-handle') ||
        btn.getAttribute('data-wish-id') ||
        btn.getAttribute('data-product-id') ||
        btn.closest('[data-product-handle]')?.getAttribute('data-product-handle') ||
        btn.closest('[data-product-id]')?.getAttribute('data-product-id');

      if (handle && isInWishlist(handle)) {
        btn.classList.add('active', 'is-active');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        btn.classList.remove('active', 'is-active');
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Add to wishlist');
      }
    });
  }

  // Delegated click event listener
  function handleDocumentClick(e) {
    const btn = e.target.closest(
      '[data-wishlist-btn], .wishlist-btn, .bluebuck-trend-wishlist, .bluebuck-wishlist-btn, .bluebuck-bs-wishlist, .rp-wishlist-btn, .bb-item-wish-btn, .product-card__wishlist, .i3d-wishlist-btn, .lt-card-wishlist-btn'
    );

    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const handle =
      btn.getAttribute('data-product-handle') ||
      btn.getAttribute('data-wish-id') ||
      btn.getAttribute('data-product-id') ||
      btn.closest('[data-product-handle]')?.getAttribute('data-product-handle') ||
      btn.closest('[data-product-id]')?.getAttribute('data-product-id');

    if (!handle) return;

    // Micro-animation bounce
    btn.classList.add('wishlist-animating');
    setTimeout(() => btn.classList.remove('wishlist-animating'), 350);

    toggleWishlist(handle);
  }

  // Initialize
  function init() {
    syncWishlistUI();
    document.addEventListener('click', handleDocumentClick, true);

    // Cross-tab synchronization
    window.addEventListener('storage', e => {
      if (e.key === PRIMARY_KEY || e.key === LEGACY_KEY) {
        syncWishlistUI();
      }
    });

    // Support dynamic re-render events
    window.addEventListener('wishlist:sync', syncWishlistUI);
    document.addEventListener('shopify:section:load', syncWishlistUI);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose global helpers
  window.BlueBuckWishlist = {
    get: getWishlist,
    isFavorited: isInWishlist,
    toggle: toggleWishlist,
    sync: syncWishlistUI
  };
  window.ShopifyLikedProducts = window.BlueBuckWishlist;
  window.syncWishlistUI = syncWishlistUI;
})();
